import {
  Trash2,
  Smartphone,
  Globe,
  Mail,
  AlertTriangle,
  Database,
  Clock,
  HelpCircle,
} from 'lucide-react';
import { LegalPageLayout } from '../components/legal/LegalPageLayout';

const sections = [
  { id: 'overview', label: 'Overview', icon: <Trash2 className="w-4 h-4" /> },
  { id: 'in-app-mobile', label: 'In-App (Mobile)', icon: <Smartphone className="w-4 h-4" /> },
  { id: 'in-app-web', label: 'In-App (Web)', icon: <Globe className="w-4 h-4" /> },
  { id: 'email', label: 'By Email', icon: <Mail className="w-4 h-4" /> },
  { id: 'what-is-deleted', label: 'What Is Deleted', icon: <Database className="w-4 h-4" /> },
  { id: 'what-is-retained', label: 'What We Retain', icon: <Clock className="w-4 h-4" /> },
  { id: 'irreversible', label: 'Cannot Be Undone', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 'help', label: 'Need Help?', icon: <HelpCircle className="w-4 h-4" /> },
];

const h2 = 'text-2xl font-semibold text-foreground mt-10';
const body = 'text-base leading-relaxed text-foreground/80';
const list = 'list-disc space-y-2 pl-6 text-foreground/80';
const ol = 'list-decimal space-y-2 pl-6 text-foreground/80';
const link = 'text-primary hover:underline';

export default function DeleteAccount() {
  return (
    <LegalPageLayout
      title="Delete Your Polymyth Account"
      lastUpdated="Last Updated: May 25, 2026"
      sections={sections}
    >
      <div className="space-y-6">
        <section id="overview" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>Overview</h2>
          <p className={body}>
            You can permanently delete your <strong>Polymyth</strong> account at any time. Deletion
            removes your Firebase Auth credentials, your characters, your campaign journal, and all
            chat history associated with your account. There are three ways to request deletion:
            from the mobile app, from the web app, or by emailing us.
          </p>
        </section>

        <section id="in-app-mobile" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>1. From the Polymyth Mobile App (iOS &amp; Android)</h2>
          <ol className={ol}>
            <li>Open Polymyth and sign in to the account you wish to delete.</li>
            <li>
              Tap the menu (☰) and select <strong>Account</strong>.
            </li>
            <li>
              Scroll to <strong>Danger Zone</strong>.
            </li>
            <li>
              Tap <strong>Delete account</strong>, type your email to confirm, and confirm the final
              prompt.
            </li>
          </ol>
          <p className={body}>
            Your account is removed within a few seconds and you are signed out immediately on every
            device.
          </p>
        </section>

        <section id="in-app-web" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>2. From the Polymyth Web App</h2>
          <ol className={ol}>
            <li>
              Visit{' '}
              <a href="https://polymyth.quest" className={link}>
                polymyth.quest
              </a>{' '}
              and sign in to the account you wish to delete.
            </li>
            <li>
              Open the user menu (top-right) → <strong>Account</strong>.
            </li>
            <li>
              Scroll to the <strong>Danger Zone</strong> section.
            </li>
            <li>
              Click <strong>Delete Account</strong>, type your email to confirm, and confirm the
              final prompt.
            </li>
          </ol>
        </section>

        <section id="email" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>3. By Email (if you cannot sign in)</h2>
          <p className={body}>
            If you have lost access to your account and cannot sign in to delete it yourself, send
            an email to{' '}
            <a href="mailto:tavian@polymyth.quest?subject=Delete%20My%20Account" className={link}>
              tavian@polymyth.quest
            </a>{' '}
            with the subject line <em>Delete My Account</em>. Send the request from the email
            address registered on the account so we can verify ownership.
          </p>
          <p className={body}>
            We will confirm the deletion within <strong>7 calendar days</strong> and complete it
            within <strong>30 calendar days</strong> of receiving a verified request.
          </p>
        </section>

        <section id="what-is-deleted" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>What Is Deleted</h2>
          <p className={body}>
            The following are permanently removed when you delete your account:
          </p>
          <ul className={list}>
            <li>Your Firebase Auth identity (email, password hash, user ID)</li>
            <li>Your display name and profile data</li>
            <li>All characters you own, including stats, inventory, and progress</li>
            <li>Your campaign journal entries</li>
            <li>All chat messages, party decisions, and roleplay history tied to your account</li>
            <li>WebAuthn credentials (passkeys) associated with your account</li>
            <li>Any pending party invites or lobby memberships</li>
            <li>References linking your purchase history to your identity</li>
          </ul>
        </section>

        <section id="what-is-retained" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>What We Retain (and for How Long)</h2>
          <p className={body}>
            A small set of records is retained after deletion for legal, financial, or operational
            reasons. None of these records identify you as an individual after deletion:
          </p>
          <ul className={list}>
            <li>
              <strong>Anonymized purchase receipts.</strong> Transaction IDs and amounts are
              retained for <strong>7 years</strong> to satisfy tax and platform billing audit
              requirements (Apple App Store and Google Play). The link to your user identity is
              severed at deletion.
            </li>
            <li>
              <strong>Aggregated, anonymized usage logs.</strong> Service-level metrics (counts,
              latencies, error rates) that contain no personally identifying information are
              retained indefinitely to operate and improve the Service.
            </li>
            <li>
              <strong>Backups.</strong> Encrypted database backups may temporarily contain your data
              for up to <strong>90 days</strong> after deletion, after which they are rotated out
              and overwritten in the normal course of business.
            </li>
          </ul>
        </section>

        <section id="irreversible" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>Account Deletion Cannot Be Undone</h2>
          <p className={body}>
            Once your account is deleted, your characters, journal, and chat history cannot be
            recovered, even if you sign up again with the same email address. Unspent Polymyth
            Tokens are forfeited at deletion and are not refundable.
          </p>
        </section>

        <section id="help" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>Need Help?</h2>
          <p className={body}>
            If you have questions about account deletion or about the data Polymyth holds, see our{' '}
            <a href="/privacy" className={link}>
              Privacy Policy
            </a>{' '}
            or contact{' '}
            <a href="mailto:tavian@polymyth.quest" className={link}>
              tavian@polymyth.quest
            </a>
            .
          </p>
        </section>

        <div className="border-t pt-8 mt-10 text-sm text-muted-foreground">
          <p>
            Polymyth is a role-playing adventure platform available on iOS, Android, and the web at{' '}
            <a href="https://polymyth.quest" className={link}>
              polymyth.quest
            </a>
            . These deletion procedures apply to all platforms.
          </p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
